import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning, CloudFog,
  CloudDrizzle, Wind, Droplets, RefreshCw, Loader2, ChevronDown, MapPin, AlertTriangle,
} from "lucide-react";

// WMO weather code → { label, icon }
const WMO = {
  0: ["Ciel dégagé", Sun], 1: ["Peu nuageux", CloudSun], 2: ["Partiellement nuageux", CloudSun],
  3: ["Couvert", Cloud], 45: ["Brouillard", CloudFog], 48: ["Brouillard givrant", CloudFog],
  51: ["Bruine légère", CloudDrizzle], 53: ["Bruine", CloudDrizzle], 55: ["Bruine dense", CloudDrizzle],
  56: ["Bruine verglaçante", CloudDrizzle], 57: ["Bruine verglaçante", CloudDrizzle],
  61: ["Pluie faible", CloudRain], 63: ["Pluie", CloudRain], 65: ["Pluie forte", CloudRain],
  66: ["Pluie verglaçante", CloudRain], 67: ["Pluie verglaçante", CloudRain],
  71: ["Neige faible", CloudSnow], 73: ["Neige", CloudSnow], 75: ["Neige forte", CloudSnow],
  77: ["Grains de neige", CloudSnow], 80: ["Averses", CloudRain], 81: ["Averses", CloudRain],
  82: ["Averses violentes", CloudRain], 85: ["Averses de neige", CloudSnow], 86: ["Averses de neige", CloudSnow],
  95: ["Orage", CloudLightning], 96: ["Orage grêle", CloudLightning], 99: ["Orage grêle", CloudLightning],
};
const wmo = (c) => WMO[c] || ["—", Cloud];

const dayName = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "short" });
const hourLabel = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit" }).replace(":00", "h");

export default function WeatherWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/weather");
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Météo indisponible");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cur = data?.current;
  const [curLabel, CurIcon] = cur ? wmo(cur.weather_code) : ["", Cloud];

  // next 24h from now
  const hours = (() => {
    if (!data?.hourly?.time) return [];
    const now = Date.now();
    const out = [];
    for (let i = 0; i < data.hourly.time.length && out.length < 24; i++) {
      if (new Date(data.hourly.time[i]).getTime() >= now - 3600_000) {
        out.push({
          t: data.hourly.time[i],
          temp: Math.round(data.hourly.temperature_2m[i]),
          code: data.hourly.weather_code[i],
          pop: data.hourly.precipitation_probability?.[i],
        });
      }
    }
    return out;
  })();

  const daily = (() => {
    if (!data?.daily?.time) return [];
    return data.daily.time.map((t, i) => ({
      t,
      code: data.daily.weather_code[i],
      max: Math.round(data.daily.temperature_2m_max[i]),
      min: Math.round(data.daily.temperature_2m_min[i]),
      pop: data.daily.precipitation_probability_max?.[i],
    }));
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 w-full lg:w-[340px] shrink-0" data-testid="weather-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <MapPin className="w-3.5 h-3.5 text-emerald-600" />
          <span data-testid="weather-city">{data?.city || "Météo"}</span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="weather-refresh"
          className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-emerald-600 hover:border-emerald-500/40 transition disabled:opacity-50"
          title="Actualiser"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-24 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 text-xs text-amber-600 py-3" data-testid="weather-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}. Cliquez sur ↻ pour réessayer.</span>
        </div>
      ) : data?.needs_city ? (
        <div className="flex items-start gap-2 text-xs text-slate-500 py-3" data-testid="weather-needs-city">
          <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
          <span>Configurez votre ville dans <a href="/settings" className="text-emerald-600 hover:underline">Paramètres → Entreprise</a> pour afficher la météo.</span>
        </div>
      ) : cur ? (
        <>
          <div className="flex items-center gap-3">
            <CurIcon className="w-11 h-11 text-emerald-600 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="text-3xl font-bold font-mono text-slate-900 leading-none" data-testid="weather-temp">
                {Math.round(cur.temperature_2m)}°
              </div>
              <div className="text-xs text-slate-500 mt-1">{curLabel}</div>
            </div>
            <div className="ml-auto text-right text-xs text-slate-500 space-y-1">
              <div>Ressenti {Math.round(cur.apparent_temperature)}°</div>
              <div className="flex items-center justify-end gap-1"><Droplets className="w-3 h-3" /> {cur.relative_humidity_2m}%</div>
              <div className="flex items-center justify-end gap-1"><Wind className="w-3 h-3" /> {Math.round(cur.wind_speed_10m)} km/h</div>
            </div>
          </div>

          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="weather-toggle"
            className="w-full mt-3 flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-emerald-600 transition"
          >
            {expanded ? "Réduire" : "Prévisions 7 jours & horaires"}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>

          {expanded && (
            <div className="mt-3 space-y-4" data-testid="weather-forecast">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Prochaines 24h</div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {hours.map((h, i) => {
                    const [, HIcon] = wmo(h.code);
                    return (
                      <div key={i} className="flex flex-col items-center gap-1 shrink-0 text-center">
                        <span className="text-[10px] text-slate-400">{hourLabel(h.t)}</span>
                        <HIcon className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                        <span className="text-xs font-mono font-medium text-slate-700">{h.temp}°</span>
                        {h.pop != null && <span className="text-[9px] text-cyan-600">{h.pop}%</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">7 jours</div>
                <div className="space-y-1">
                  {daily.map((d, i) => {
                    const [dLabel, DIcon] = wmo(d.code);
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="w-9 text-slate-500 capitalize">{i === 0 ? "Auj." : dayName(d.t)}</span>
                        <DIcon className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={1.5} />
                        <span className="flex-1 text-slate-400 truncate">{dLabel}</span>
                        {d.pop != null && <span className="text-[10px] text-cyan-600">{d.pop}%</span>}
                        <span className="font-mono text-slate-900 font-medium w-8 text-right">{d.max}°</span>
                        <span className="font-mono text-slate-400 w-8 text-right">{d.min}°</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
